import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DPCard from "../../packages/DPCard";
import WPTertiaryButton from "../../packages/WPTertiaryButton";
import CustomModal from "../../packages/CustomModal";
import FileHandler from "./FileUploadModal";
import {
  CustomModalButtons,
  DATA_APPEND_CONSTANTS,
  DATA_APPEND_WARNING_SEVERITY_TYPES,
} from "../../constants/global.constants";
import { useToastNotification } from "../../packages/WPToastNotification/WPToastNotificationProvider";
import {
  downloadDatasetFile,
  deleteAppendFile,
  appendExistingFile,
  getDataVisualization,
} from "../../network/cipAPI";
import FileDetailsCard from "../FileUploadCard/FileDetailsCard";
import TableIcon from "../../packages/SVGs/Table";
import { navigateToCreateNewPlant } from "../../utils/Utils";
import ConfirmationPopup, {
  DIALOG_TYPES,
} from "../../packages/ConfirmationPopup";
import "./DataAppend.scss";
import { dataAppend, append } from "../../assets";
import DataWarnings from "./DataWarnings";
import createNotification, {
  ERROR_NOTIFICATION,
} from "../../components/Notifications";
import DataVisualizationTable from "../../pages/analyseData/DataVisualizationTable";

const DataAppend = ({
  id,
  title,
  content,
  buttonText,
  linkText,
  fileId,
  selectedTrain,
  setIsLoading,
  fileName,
  className,
  mappedReference,
  isappendFile,
  appendFileName,
  setAppendFileName,
  setIsAppendFile,
  appendStatus,
  setAppendStatus,
  setTempReferenceValues,
  ...props
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showSuccessNotification, showErrorNotification } =
    useToastNotification();
  const [isFileUploadModalOpen, setIsFileUploadModalOpen] = useState(false);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [formattedErrorContent, setFormattedErrorContent] = useState(null);
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [errorLoading, setErrorLoading] = useState(false);
  const [viewLoading, setviewLoading] = useState(false);
  const [appendErrorSource, setAppendErrorSource] = useState(null); // 'large data gap error ' or 'no new data found eror'
  const [errorSource, setErrorSource] = useState(null); // 'upload' or 'append'
  const [warningRes, setWarningRes] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [viewAppendDialog, setViewAppendDialog] = useState(false);
  const [dataVisualisationRow, setDataVisualisationRow] = useState([]);

  // Simple content type mapping for file extensions
  const getFileExtension = (contentType) => {
    if (contentType?.includes("spreadsheetml")) return "xlsx";
    if (contentType?.includes("excel")) return "xls";
    if (contentType?.includes("csv")) return "csv";
    return "xlsx";
  };

  const openDataVisualizationPopup = async () => {
    setviewLoading(true);
    try {
      const response = await getDataVisualization(
        fileId,
        selectedTrain,
        appendFileName
      );
      if (response && response.data) {
        if (response.data.detail && response.data.detail.error) {
          setDataVisualisationRow([]);
          createNotification(
            ERROR_NOTIFICATION,
            t("notificationStrings.somethingWrong"),
            ""
          );
        } else {
          setDataVisualisationRow(response.data);
        }
      }
    } catch (err) {
      setDataVisualisationRow([]);
      createNotification(
        ERROR_NOTIFICATION,
        t("notificationStrings.somethingWrong"),
        ""
      );
      console.log("openDataVisualizationPopup", err);
    } finally {
      setviewLoading(false);
      return setViewAppendDialog(true);
    }
  };
  // Helper function to trigger file download
  const triggerDownload = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleReplaceAppendedFile = () => {
    setIsFileUploadModalOpen(true);
  };
  const handleAppendRemove = async (setLoader) => {
    await deleteFileAndResetState(selectedTrain, setLoader);
    setAppendFileName(null);
    setIsAppendFile(false);
  };

  const handleDatasetDownload = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await downloadDatasetFile(selectedTrain);

      if (response?.status !== 200 || !response?.data) {
        showErrorNotification(t("notificationStrings.somethingWrong"));
        return;
      }

      const contentType =
        response.headers?.["content-type"] ||
        response.headers?.["Content-Type"];
      const fileExtension = getFileExtension(contentType);
      const baseName = fileName || t("dataAppend.defaultFileName");
      const nameWithoutExt = baseName.replace(/\.[^/.]+$/, "");
      const downloadFileName = `${nameWithoutExt}.${fileExtension}`;

      triggerDownload(response.data, downloadFileName);
    } catch (error) {
      console.error("Download failed:", error);
      showErrorNotification(t("notificationStrings.somethingWrong"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleTertiaryButtonClick = () => {
    setIsFileUploadModalOpen(true);
  };

  const handleModalClose = async ({ target }) => {
    const id = target?.id;
    setShowErrorPopup(false);
    setFormattedErrorContent(null);
    setErrorSource(null);
    if (id === CustomModalButtons.CONFIRM) {
      await handleAppendExistingFile(uploadedFile.name, true);
      setUploadedFile(null);
      setWarningRes(null);
    } else if (id === CustomModalButtons.CANCEL) {
      await handleDeleteFile();
    } else {
      setIsFileUploadModalOpen(false);
    }
  };

  const handleFileValidation = (result) => {
    if (result?.success) {
      showSuccessNotification(
        result.message || t("notificationStrings.fileUploadSuccess")
      );
    } else if (result?.error && result?.formattedContent) {
      setFormattedErrorContent(result.formattedContent);
      setErrorSource(DATA_APPEND_CONSTANTS.UPLOAD);
      setShowErrorPopup(true);
      setIsFileUploadModalOpen(false);
      setUploadedFile(null);
    } else if (result?.error) {
      showErrorNotification(result.error);
      setIsFileUploadModalOpen(false);
      setUploadedFile(null);
    }
  };

  const handleErrorPopupClose = async (buttonId) => {
    if (buttonId === CustomModalButtons.CONFIRM) {
      navigateToCreateNewPlant(navigate);
    } else if (buttonId === CustomModalButtons.CANCEL) {
      if (errorSource === DATA_APPEND_CONSTANTS.APPEND) {
        await handleDeleteFile();
      } else {
        setShowErrorPopup(false);
        setFormattedErrorContent(null);
        setErrorSource(null);
        setIsFileUploadModalOpen(true);
        setTimeout(() => {
          const modalBody = document.querySelector(
            ".modal-body-container-append"
          );
          if (modalBody) {
            const input = modalBody.querySelector('input[type="file"]');
            if (input) input.click(); // trigger native file picker
          }
        }, 200); // small delay
      }
    } else if (buttonId === CustomModalButtons.CLOSE_BTN) {
      setIsFileUploadModalOpen(true);
      setShowErrorPopup(false);
      setFormattedErrorContent(null);
      setErrorSource(null);
    }
  };

  const deleteFileAndResetState = async (selectedTrain, setLoader) => {
    try {
      setLoader(true);
      const response = await deleteAppendFile(selectedTrain);
      if (response && response.message) {
        showSuccessNotification(response.message);
        setUploadedFile(null);
        setWarningRes(null);
        setAppendStatus("idle");
        setAppendFileName(null);
        setIsAppendFile(false);
      } else {
        const errorMsg = response?.message || t("dataAppend.deleteFail");
        showErrorNotification(errorMsg);
      }
    } catch (error) {
      showErrorNotification(
        error.message || t("notificationStrings.somethingWrong")
      );
    } finally {
      setLoader(false);
    }
  };
  const handleDeleteFile = async () => {
    await deleteFileAndResetState(selectedTrain, setErrorLoading);
    setShowErrorPopup(false);
    setFormattedErrorContent(null);
    setErrorSource(null);
    setIsFileUploadModalOpen(true);
  };

  const handleAppendResult = (result) => {
    setIsModalLoading(false);

    if (result?.success) {
      setIsFileUploadModalOpen(false);
      setUploadedFile(null);
      showSuccessNotification(
        result.message || t("notificationStrings.fileUploadSuccess")
      );
    } else if (result?.error && result?.formattedContent) {
      // Set error content and show popup
      setFormattedErrorContent(result.formattedContent);
      setErrorSource(DATA_APPEND_CONSTANTS.APPEND);
      setShowErrorPopup(true);
      setUploadedFile(null);
      setIsFileUploadModalOpen(false);
      if (result.showNotification) {
        showErrorNotification(result.error);
      }
    } else if (result?.has_warnings) {
      setWarningRes(result);
    } else if (result?.error) {
      showErrorNotification(result.error);
      setIsFileUploadModalOpen(false);
      setUploadedFile(null);
    }
  };

  const errorPopupHeader = useMemo(
    () =>
      errorSource === DATA_APPEND_CONSTANTS.APPEND
        ? appendErrorSource === "large data"
          ? t("dataAppend.largeDataGapHeader")
          : t("dataAppend.noNewDataHeader")
        : t("dataAppend.appendDataFailed"),
    [errorSource, t]
  );

  const errorPopupCancelBtn = useMemo(
    () =>
      errorSource === DATA_APPEND_CONSTANTS.APPEND
        ? appendErrorSource === "no new data"
          ? t("ValueStrings.cancel")
          : t("dataAppend.cancelAppend")
        : t("dataAppend.uploadAnotherFile"),
    [errorSource, appendErrorSource, t]
  );

  const errorPopupLinkBtn = useMemo(
    () =>
      errorSource === DATA_APPEND_CONSTANTS.UPLOAD
        ? t("dataAppend.cancelAppend")
        : undefined,
    [errorSource, t]
  );

  // Memoize confirmBtn label outside of JSX
  const confirmBtnLabel = useMemo(() => t("dataAppend.createNewPlant"), [t]);

  const handleAppendExistingFile = async (
    filename,
    proceedOnWarning = false
  ) => {
    let result = null;

    try {
      setIsModalLoading(true);

      const response = await appendExistingFile(
        selectedTrain,
        filename,
        proceedOnWarning
      );

      if (response?.status === 200) {
        setAppendFileName(response?.filename);
        setIsAppendFile(response?.isAppend);
        setAppendStatus("success");
        result = {
          success: true,
          message: response.message,
        };
      } else if (response?.status === 422 || response?.status === 400) {
        setAppendStatus("error");
        // Check for critical warnings first
        if (response.detail?.has_warnings && response.detail?.warnings) {
          const { warnings, data_summary, has_warnings } = response.detail;
          const criticalWarning = warnings.find(
            (warning) =>
              warning.severity === DATA_APPEND_WARNING_SEVERITY_TYPES.CRITICAL
          );
          const gapWarning = warnings.find(
            (warning) =>
              warning.severity === DATA_APPEND_WARNING_SEVERITY_TYPES.WARNING
          );
          const overlapWarning = warnings.find(
            (warning) =>
              warning.severity === DATA_APPEND_WARNING_SEVERITY_TYPES.INFO
          );

          if (criticalWarning) {
            const criticalWarningMessage = criticalWarning.message;
            setAppendErrorSource("large data");
            const formattedContent = (
              <div>
                <div className="marginBtm10">{criticalWarningMessage}</div>
                <div>{t("dataAppend.newPlantText")} </div>
              </div>
            );
            result = {
              error: criticalWarningMessage,
              formattedContent,
              showNotification: false,
            };
          } else if (gapWarning || overlapWarning) {
            result = { gapWarning, overlapWarning, data_summary, has_warnings };
          }
        } else if (response.detail?.key === "NO_DATA_FOUND") {
          setAppendErrorSource("no new data");
          const message = response?.detail?.error;
          const formattedContent = (
            <div>
              <div className="marginBtm10">{t("dataAppend.noNewDataBody")}</div>
              <div>{t("dataAppend.noNewDataSuggestion")}</div>
            </div>
          );
          result = {
            error: message,
            formattedContent,
            showNotification: false,
          };
        } else {
          const errorMessage =
            response.detail?.error ||
            response.message ||
            t("dataAppend.invalidFileError");
          result = {
            error: errorMessage,
            showNotification: true,
          };
        }
      } else {
        setAppendStatus("error");
        result = { error: t("ValueStrings.somethingWentWrong") };
      }
    } catch (error) {
      setAppendStatus("error");
      result = { error: error.message || t("dataAppend.appendError") };
    } finally {
      handleAppendResult(result);
    }
  };

  const handleViewAppendTableOpen = () => {
    setViewAppendDialog(true);
    openDataVisualizationPopup();
  };
  const handleViewAppendTableClose = () => {
    setViewAppendDialog(false);
  };
  return (
    <>
      {isappendFile && appendFileName ? (
        <div className="ro-card-wrapper">
          <div className={"teal-heading header-border-bottom flex-between"}>
            {t("dataAppend.appendHeaderSuccess")}
            <WPTertiaryButton
              variant="text"
              label={t("dataAppend.viewAppend")}
              icon={<TableIcon />}
              iconPlacement="before"
              size="small"
              onClick={handleViewAppendTableOpen}
            />
          </div>
          <FileDetailsCard
            uploadedFile={{ name: appendFileName }}
            setUploadedFile={setUploadedFile}
            isUploadComplete={isappendFile}
            hideUploadButton={true}
            showAppend={false}
            showDownload={false}
            appendStatus={appendStatus}
            showRemoveIcon={true}
            handleAppendReplace={handleReplaceAppendedFile}
            handleRemoveFile={() => handleAppendRemove(setIsLoading)}
          />
        </div>
      ) : (
        <DPCard
          id={id}
          disabled={Boolean(!mappedReference)}
          className={`data-append-card ${className || ""}`}
          {...props}
        >
          <div className="data-append-wrapper">
            <div className="data-append-icon">
              {/* Main append icon from external SVG file */}
              <img src={append} alt="Append Data" />
            </div>

            {/* Middle Text Content */}
            <div className="data-append-content">
              <div className="data-append-header">
                {title && <p className="data-append-title">{title}</p>}
              </div>
              <div className="data-append-body">
                {linkText ? (
                  <p className="data-append-text">
                    {content}{" "}
                    <a
                      className="data-append-link"
                      style={{ cursor: "pointer" }}
                      onClick={
                        !mappedReference ? undefined : handleDatasetDownload
                      }
                    >
                      {linkText}
                    </a>
                    .
                  </p>
                ) : (
                  <p className="data-append-text">{content}</p>
                )}
              </div>
            </div>

            {/* Button Section */}
            <div className="data-append-button">
              <WPTertiaryButton
                id="append-data-btn"
                label={buttonText}
                icon={<img src={dataAppend} alt="Data Append Icon" />}
                iconPlacement="before"
                className="data-append-tertiary-btn"
                variant="text"
                onClick={handleTertiaryButtonClick}
                disabled={Boolean(!mappedReference)}
              />
            </div>
          </div>
        </DPCard>
      )}

      {/* File Upload Modal */}
      {isFileUploadModalOpen && (
        <CustomModal
          header={t("dataAppend.appendData")}
          onModalClose={handleModalClose}
          custombodyclass={"modal-body-container-append"}
          isLoading={isModalLoading}
          hideFooter={warningRes === null}
          confirmBtn={t("ValueStrings.continueAnyway")}
          cancelBtn={t("dataAppend.cancelAppend")}
        >
          {warningRes === null ? (
            <FileHandler
              onFileValidation={handleFileValidation}
              handleAppendExistingFile={handleAppendExistingFile}
              selectedTrain={selectedTrain}
              uploadedFile={uploadedFile}
              setUploadedFile={setUploadedFile}
              handleAppendRemove={() => handleAppendRemove(setIsModalLoading)}
            />
          ) : (
            <DataWarnings
              gapWarning={warningRes.gapWarning}
              overlapWarning={warningRes.overlapWarning}
              summaryData={warningRes.data_summary}
              selectedTrain={selectedTrain}
            />
          )}
        </CustomModal>
      )}

      {/* Error Confirmation Popup */}
      {showErrorPopup && (
        <ConfirmationPopup
          header={errorPopupHeader}
          description={formattedErrorContent}
          type={DIALOG_TYPES.ERROR}
          onClose={handleErrorPopupClose}
          confirmBtn={confirmBtnLabel}
          cancelBtn={errorPopupCancelBtn}
          linkBtn={errorPopupLinkBtn}
          width="700px"
          isLoading={errorLoading}
        />
      )}
      {viewAppendDialog && (
        <DataVisualizationTable
          open={viewAppendDialog}
          onClose={handleViewAppendTableClose}
          title={t("dataAppend.viewAppend")}
          rows={dataVisualisationRow}
          setshowDataVisualizationPopup={setViewAppendDialog}
          setTempReferenceValues={setTempReferenceValues}
          isLoading={viewLoading}
        />
      )}
    </>
  );
};

export default DataAppend;
